import { useState } from 'react';

/**
 * Custom hook to manage arrow key navigation in a list.
 * @param {number} maxIndex - The maximum index in the list.
 * @param {object} options - Additional options for navigation.
 * @returns {object} An object containing the highlighted index and navigation functions.
 */
const useArrowNavigation = (maxIndex, options = {}) => {
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const {
    isOpen = true,
    onItemSelect = () => {},
    onClose = () => {},
    getItemByIndex = () => {},
    selectionKeys = ['Enter'],
  } = options;

  const incrementIndex = () => {
    setHighlightedIndex((prevIndex) =>
      prevIndex < maxIndex ? prevIndex + 1 : 0
    );
  };

  const decrementIndex = () => {
    setHighlightedIndex((prevIndex) =>
      prevIndex > 0 ? prevIndex - 1 : maxIndex
    );
  };

  const resetIndex = () => {
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      incrementIndex();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      decrementIndex();
    } else if (selectionKeys.includes(e.key)) {
      e.preventDefault();
      if (highlightedIndex >= 0) {
        const item = getItemByIndex(highlightedIndex);
        onItemSelect(item);
      }
    } else if (e.key === 'Escape') {
      onClose();
      resetIndex();
    }
  };

  return {
    highlightedIndex,
    setHighlightedIndex,
    resetIndex,
    handleKeyDown,
  };
};

export default useArrowNavigation;
